import { useState } from "react";
import { Check, Pencil, RotateCcw, Send, Star, Trash2, X } from "lucide-react";
import { MEAL_TYPE_LABELS, type LoggedMeal } from "../lib/types";
import ShareMealModal from "./ShareMealModal";

interface Props {
  meals: LoggedMeal[];
  onDelete: (id: string) => void;
  onLogAgain: (meal: LoggedMeal) => void;
  onEdit: (id: string) => void;
  onFavorite: (meal: LoggedMeal) => void;
  recentlyLoggedSourceId?: string | null;
}

// localStorage key for one-time icon-row hint dismissal. The four icons on
// each meal row are non-obvious to first-time users — this banner names them
// once, then gets out of the way forever.
// v2 — bumped from v1 because we added the Share icon to the lineup; users
// who already dismissed the original hint should see the new one once.
const ICON_HINT_KEY = "tallyplate.mealLog.iconHintDismissed.v2";

export default function MealLog({ meals, onDelete, onLogAgain, onEdit, onFavorite, recentlyLoggedSourceId }: Props) {
  const [shareMeal, setShareMeal] = useState<LoggedMeal | null>(null);
  const [showHint, setShowHint] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(ICON_HINT_KEY) !== "1";
    } catch {
      return false;
    }
  });

  function dismissHint() {
    setShowHint(false);
    try {
      localStorage.setItem(ICON_HINT_KEY, "1");
    } catch {
      /* private mode — ignore */
    }
  }

  if (meals.length === 0) {
    return <div className="p-8 text-center text-slate-500">No meals logged yet.</div>;
  }
  return (
    <>
    {showHint && (
      <div className="mx-4 mb-2 p-2.5 rounded-lg bg-slate-900/80 border border-slate-800 flex items-start gap-3 text-[12px] text-slate-300">
        <div className="flex-1 leading-relaxed">
          <span className="text-slate-400">Tip:</span> the icons on each meal —{" "}
          <Star className="w-3 h-3 inline align-text-bottom text-amber-400" /> favorite ·{" "}
          <Send className="w-3 h-3 inline align-text-bottom text-emerald-300" /> share ·{" "}
          <Pencil className="w-3 h-3 inline align-text-bottom text-emerald-300" /> edit ·{" "}
          <RotateCcw className="w-3 h-3 inline align-text-bottom text-emerald-300" /> log again ·{" "}
          <Trash2 className="w-3 h-3 inline align-text-bottom text-rose-400" /> delete.
        </div>
        <button
          onClick={dismissHint}
          className="text-slate-500 hover:text-slate-300 flex-shrink-0"
          aria-label="Dismiss tip"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )}
    <ul className="space-y-2 p-4">
      {meals.map((m) => (
        <li key={m.id} className="p-3 rounded-lg bg-slate-900 border border-slate-800">
          <div className="flex gap-3">
            {m.photo_data_url && (
              <img src={m.photo_data_url} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                    {MEAL_TYPE_LABELS[m.meal_type]}
                  </span>
                  <span>{formatTime(m.logged_at)}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => onFavorite(m)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-amber-400 hover:bg-slate-800"
                    title="Save as favorite"
                    aria-label="Save as favorite"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShareMeal(m)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-emerald-300 hover:bg-slate-800"
                    title="Share with a partner"
                    aria-label="Share with a partner"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(m.id)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-emerald-300 hover:bg-slate-800"
                    title="Edit"
                    aria-label="Edit meal"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onLogAgain(m)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-800 ${
                      recentlyLoggedSourceId === m.id ? "text-emerald-400" : "text-slate-500 hover:text-emerald-300"
                    }`}
                    title="Log again"
                    aria-label="Log again"
                  >
                    {recentlyLoggedSourceId === m.id ? <Check className="w-4 h-4 text-emerald-400" /> : <RotateCcw className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => onDelete(m.id)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-rose-400 hover:bg-slate-800"
                    title="Delete"
                    aria-label="Delete meal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="text-base font-medium truncate mt-1">
                {m.estimate.items.map((i) => i.name).join(", ")}
              </div>
              {/* Portion line — surfaces actual measurements ("150 g, 1 cup")
                  so the History view shows how much was logged, not just what. */}
              {m.estimate.items.some((i) => i.portion) && (
                <div className="text-xs text-slate-400 mt-0.5 truncate">
                  {m.estimate.items.map((i) => i.portion).filter(Boolean).join(" · ")}
                </div>
              )}
              <div className="text-sm text-slate-300 mt-0.5">
                {Math.round(m.estimate.totals.calories)} kcal
                <span className="text-slate-500"> · </span>
                <span className="text-sky-400">Protein</span> {m.estimate.totals.protein_g.toFixed(0)}g
                <span className="text-slate-500"> · </span>
                <span className="text-amber-400">Carbs</span> {m.estimate.totals.carbs_g.toFixed(0)}g
                <span className="text-slate-500"> · </span>
                <span className="text-violet-400">Fat</span> {m.estimate.totals.fat_g.toFixed(0)}g
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
    {shareMeal && <ShareMealModal meals={[shareMeal]} onClose={() => setShareMeal(null)} />}
    </>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}
